import { Interceptor, Transport } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';

import authClient from '../authClient';

/**
 * Used with Buf Connect transport to set JWT from authClient
 *
 * Authorization always carries the operator's token — services authorize RPC access on it, so this
 * overwrites anything a caller set. A credential for someone other than the operator (e.g. the
 * guest's profile JWT for CateringService.UpdateProfile) must travel on its own header.
 * @param next Interceptors "onion" entry point
 */
const headerInterceptor: Interceptor = (next) => async (req) => {
  const token = await authClient.getOrRenewAccessToken();
  req.header.set('Authorization', `JWTBearer ${token}`);
  return await next(req);
};

/**
 * Transport cache keyed by baseUrl
 */
const transportCache = new Map<string, Transport>();

/**
 * Gets or creates a ConnectRPC transport for the specified URL.
 * Transports are cached per URL to avoid creating duplicate instances.
 * All transports share the same JWT authentication interceptor.
 *
 * @param baseUrl The base URL for the gRPC/Connect service
 * @returns A Transport instance configured for the given URL
 */
export function getTransportForUrl(baseUrl: string): Transport {
  if (!transportCache.has(baseUrl)) {
    transportCache.set(
      baseUrl,
      createConnectTransport({
        baseUrl,
        interceptors: [headerInterceptor],
      }),
    );
  }
  return transportCache.get(baseUrl)!;
}

/**
 * Clears the transport cache. Useful for testing or when authentication changes require new transports.
 */
export function clearTransportCache(): void {
  transportCache.clear();
}
